import React, {
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import ReactAce, { IAceEditorProps } from 'react-ace'
import { Ace } from 'ace-builds'
import './AceEditorConfig'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/mode-sql'
import 'ace-builds/src-noconflict/theme-sqlserver'
import 'ace-builds/src-noconflict/ext-searchbox'
import langTools, {
	keyWordCompleter,
	snippetCompleter,
} from 'ace-builds/src-noconflict/ext-language_tools'
import { FormFieldContext, withFormField } from '../UberForm/FormFieldContext'
import { useAppContext, useAppStore, useDebounce } from '@/hooks'
import { logError } from '@/utils/errorHandling'
import { AutocompleteConfig, Search } from './types'
import { useAutocomplete } from './hooks/useAutocomplete'
import { configureAceEditor, isTokenColumn } from './utils'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { mainColors } from '@/styles'
import { Modal } from '@/components'
import { EditorModalHeader } from '@/components/EditorModalHeader/EditorModalHeader'
import AutoSizer from 'react-virtualized-auto-sizer'
import Button from '../Button/Button'
import { EditorRoot, Maximizer, MaximizeToolbar, Wrapper } from './styles'
import { TbWindowMaximize } from 'react-icons/tb'

const ROW_HEIGHT = 14

export type AceEditorProps = Omit<IAceEditorProps, 'height'> &
	FormFieldContext & {
		autoFocus?: boolean
		autocompleteConfig?: AutocompleteConfig
		/** Attribute name shown in the maximized editor header. */
		title?: string
		/** Element/row identity shown next to the attribute in the maximized editor header. */
		elementLabel?: React.ReactNode
		height?: number | string
		isInModal?: boolean
		/** Show maximize control (default: true when not in modal). */
		maximizeButtonVisible?: boolean
		/** Inline row above the editor wrapper, or host renders the control via `onMaximizeReady`. */
		maximizeButtonPlacement?: 'inline' | 'external'
		onMaximizeReady?: (toggleMaximize: () => void) => void
	}

export const AceEditorWithoutForm = forwardRef(
	(props: AceEditorProps, refForwarded) => {
		const {
			name,
			value,
			onChange,
			disabled,
			height,
			mode,
			autoFocus,
			onBlur,
			isInModal,
			maximizeButtonVisible = true,
			maximizeButtonPlacement = 'inline',
			onMaximizeReady,
			autocompleteConfig,
			onKeyPress,
			title,
			elementLabel,
			...propsRest
		} = props

		const { t } = useAppContext()
		const refLocal = useRef<ReactAce | null>(null)

		const ref = useMemo(
			() =>
				(refForwarded ?? refLocal) as React.MutableRefObject<ReactAce | null>,
			[refForwarded],
		)

		const { autocompleteKeywords, autocompleteSnippets, autocompleteElements } =
			useAppStore((state) => state.settings.userInterface.scriptEditor)

		const [maximized, setMaximized] = useState(false)
		const showInlineMaximizeButton =
			maximizeButtonVisible &&
			maximizeButtonPlacement === 'inline' &&
			!isInModal &&
			!maximized
		const exposeExternalMaximize =
			maximizeButtonVisible &&
			maximizeButtonPlacement === 'external' &&
			!isInModal
		const [heightEditor, setHeightEditor] = useState(height)
		const [search, setSearch] = useState<Search | undefined>(undefined)
		const searchDebounced = useDebounce(search, 150)

		const { tables, columns } = useAutocomplete(
			searchDebounced,
			autocompleteConfig,
		)

		useEffect(() => {
			setHeightEditor(height)
		}, [height])

		useEffect(() => {
			if (autoFocus) {
				ref.current?.editor.focus()
			}
		}, [autoFocus, ref])

		useEffect(() => {
			const autocompleters = [
				autocompleteElements
					? {
							getCompletions: (
								editor: Ace.Editor,
								session: Ace.EditSession,
								pos: Ace.Point,
								prefix: string,
								callback: Ace.CompleterCallback,
							): void => {
								callback(null, [
									...columns.map(
										(column) =>
											({
												caption: `${column.code} (${column.name})`,
												value: column.code,
												score: Number.MAX_VALUE,
												meta: t('COLUMN'),
											}) as Ace.Completion,
									),
									...(search?.column === undefined
										? tables.map(
												(table) =>
													({
														caption: `${table.code} (${table.name})`,
														value: table.code,
														score: Number.MAX_VALUE,
														meta: t('TABLE'),
													}) as Ace.Completion,
											)
										: []),
								])
							},
						}
					: undefined,
				autocompleteSnippets ? snippetCompleter : undefined,
				autocompleteKeywords ? keyWordCompleter : undefined,
			].filter((autocompleter) => autocompleter !== undefined)

			langTools.setCompleters(autocompleters)

			const editor = ref.current?.editor

			if (editor) {
				editor.completer?.showPopup?.(editor)
			}
		}, [
			autocompleteElements,
			autocompleteKeywords,
			autocompleteSnippets,
			columns,
			ref,
			search,
			t,
			tables,
		])

		useEffect(() => {
			const editor = ref.current?.editor

			if (height || !editor) {
				return
			}

			const rows = editor.session.getLength()

			if (!value || rows <= 3) {
				setHeightEditor(ROW_HEIGHT * 3)

				return
			}

			setHeightEditor(Math.min(ROW_HEIGHT * 20, ROW_HEIGHT * rows))
		}, [value, height, ref])

		const onChangeHandler = useCallback(
			(value: string) => {
				const editor = ref.current?.editor
				onChange?.(value)

				if (!editor) {
					return
				}

				try {
					const { row, column } = editor.getCursorPosition()

					const tokens = editor.session.getTokens(row)
					const token = editor.session.getTokenAt(row, column + 1)

					let tokenIndex = tokens.findIndex(
						(tokenItem) =>
							tokenItem.value === token?.value &&
							tokenItem.start === token.start &&
							tokenItem.index === token.index,
					)
					tokenIndex = tokenIndex === -1 ? tokens.length - 1 : tokenIndex

					const tokenPrevious = tokens[tokenIndex - 1]
					const tokenTable = tokens[tokenIndex - 2]
					const previousValue = tokenPrevious?.value ?? ''

					if (isTokenColumn(token?.value)) {
						setSearch({ table: previousValue, column: '' })
					} else if (isTokenColumn(previousValue)) {
						setSearch({ table: tokenTable.value, column: token?.value })
					} else {
						setSearch({ table: token?.value, column: undefined })
					}
				} catch (e) {
					logError(e, { operation: 'AceEditor.onChangeHandler' })
				}
			},
			[onChange, ref],
		)

		const onMaximizeToggle = useCallback(() => {
			setMaximized((prev) => !prev)
		}, [])

		useEffect(() => {
			if (!exposeExternalMaximize || !onMaximizeReady) {
				return
			}

			onMaximizeReady(onMaximizeToggle)

			return () => {
				onMaximizeReady(() => {})
			}
		}, [exposeExternalMaximize, onMaximizeReady, onMaximizeToggle])

		const onBlurHandler = useCallback(
			(event: React.FocusEvent<HTMLElement>, editor?: Ace.Editor) => {
				if (maximized) {
					return
				}

				onBlur?.(event, editor)
			},
			[maximized, onBlur],
		)

		return (
			<>
				{maximized ? (
					<Modal
						resizable
						onClose={onMaximizeToggle}
						maximized
						open
						header={
							title || elementLabel ? (
								<EditorModalHeader
									attributeLabel={title}
									elementLabel={elementLabel}
								/>
							) : (
								<></>
							)
						}
						footer={
							<Button
								onClick={onMaximizeToggle}
								schema="transparent"
								icon={faTimes}
								style={{ marginLeft: 'auto' }}
							>
								{t('MINIMIZE')}
							</Button>
						}
					>
						<AutoSizer>
							{({ height, width }: { height: number; width: number }) => (
								<AceEditorWithoutForm
									ref={ref}
									{...props}
									height={height}
									width={`${width}px`}
									onBlur={onBlurHandler}
									autoFocus
									isInModal
								/>
							)}
						</AutoSizer>
					</Modal>
				) : (
					// handle keypress here as ReactAce does not support it
					<EditorRoot data-testid="ace-editor">
						{showInlineMaximizeButton && (
							<MaximizeToolbar>
								<Maximizer
									onClick={onMaximizeToggle}
									title={t('MAXIMIZE')}
									role="button"
									tabIndex={0}
									data-testid="ace-editor-maximize"
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault()
											onMaximizeToggle()
										}
									}}
								>
									<TbWindowMaximize size={20} color={mainColors.primary} />
								</Maximizer>
							</MaximizeToolbar>
						)}
						<Wrapper disabled={disabled} onKeyPress={onKeyPress}>
							<ReactAce
								ref={(ace) => (ref.current = ace)}
								mode={mode ?? 'sql'}
								theme={'sqlserver'}
								onChange={onChangeHandler}
								name={name}
								editorProps={{
									$blockScrolling: Infinity,
								}}
								setOptions={{
									enableBasicAutocompletion: true,
									enableLiveAutocompletion: true,
									enableSnippets: true,
									printMargin: false,
									readOnly: disabled,
								}}
								value={value ?? ''}
								onLoad={(editor) => configureAceEditor(editor, { isInModal })}
								width="100%"
								height={
									heightEditor
										? typeof heightEditor === 'number'
											? `${heightEditor}px`
											: heightEditor
										: '60px'
								}
								readOnly={disabled}
								onBlur={onBlurHandler}
								fontSize="12px"
								{...propsRest}
							/>
						</Wrapper>
					</EditorRoot>
				)}
			</>
		)
	},
)

export const AceEditor = withFormField(AceEditorWithoutForm)
